import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, CheckSquare, BarChart3, Paperclip, PenTool,
  TrendingUp, TrendingDown, AlertCircle, Clock,
  GraduationCap, Calendar, Award, MessageSquare, ChevronLeft, User,
  Megaphone, BookOpen, FileText, Pin, FlaskConical,
  Sparkles, Zap, Crown, Check, Star,
} from 'lucide-react';
import client from '../../api/client';

// ── Constants ──────────────────────────────────────────────────────────────

const TABS = [
  { id: 'dashboard',     label: 'Overview',       icon: BarChart3 },
  { id: 'attendance',    label: 'Attendance',      icon: CheckSquare },
  { id: 'marks',         label: 'Marks',           icon: Award },
  { id: 'tests',         label: 'Tests',           icon: FlaskConical },
  { id: 'assignments',   label: 'Assignments',     icon: Paperclip },
  { id: 'homework',      label: 'Homework',        icon: PenTool },
  { id: 'coaching',      label: 'Coaching',        icon: Star },
  { id: 'announcements', label: 'Announcements',   icon: Megaphone },
  { id: 'materials',     label: 'Materials',       icon: BookOpen },
  { id: 'premium',       label: 'Premium',         icon: Sparkles, highlight: true },
];

// ── Helpers ────────────────────────────────────────────────────────────────

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function pctColor(pct) {
  if (pct >= 90) return 'text-emerald-600';
  if (pct >= 75) return 'text-indigo-600';
  if (pct >= 60) return 'text-amber-600';
  return 'text-red-600';
}

function pctBg(pct) {
  if (pct >= 90) return 'bg-emerald-500';
  if (pct >= 75) return 'bg-indigo-500';
  if (pct >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

// ── Shared UI ─────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Empty({ text, icon: Icon = AlertCircle }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-slate-300" strokeWidth={2} />
      </div>
      <p className="text-sm text-slate-400 font-medium">{text}</p>
    </div>
  );
}

function SectionCard({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-100 overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="px-5 py-3.5 border-b border-slate-100">
      <h3 className="font-sora font-bold text-slate-700 text-sm">{title}</h3>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Overview stat card ─────────────────────────────────────────────────────

const STAT_STYLES = {
  green:  { card: 'bg-emerald-50 border-emerald-100', icon: 'bg-emerald-100 text-emerald-600', val: 'text-emerald-700' },
  indigo: { card: 'bg-indigo-50 border-indigo-100',   icon: 'bg-indigo-100 text-indigo-600',   val: 'text-indigo-700' },
  amber:  { card: 'bg-amber-50 border-amber-100',     icon: 'bg-amber-100 text-amber-600',     val: 'text-amber-700' },
  red:    { card: 'bg-red-50 border-red-100',         icon: 'bg-red-100 text-red-600',         val: 'text-red-700' },
  slate:  { card: 'bg-slate-50 border-slate-100',     icon: 'bg-slate-100 text-slate-500',     val: 'text-slate-700' },
};

function StatCard({ label, value, sub, color = 'slate', icon: Icon, badge }) {
  const s = STAT_STYLES[color];
  return (
    <div className={`rounded-2xl border p-4 flex flex-col gap-3 ${s.card}`}>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${s.icon}`}>
          <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" strokeWidth={2.5} />
        </div>
        {badge && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 leading-snug">
            {badge}
          </span>
        )}
      </div>
      <div>
        <p className={`text-2xl font-extrabold font-sora leading-none ${s.val}`}>{value ?? '—'}</p>
        <p className="text-xs font-semibold text-slate-600 mt-1">{label}</p>
        {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Child selector ─────────────────────────────────────────────────────────

function ChildAvatar({ name, size = 44 }) {
  return (
    <div
      className="rounded-2xl bg-indigo-100 text-indigo-700 font-bold font-sora flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {initials(name)}
    </div>
  );
}

function ChildChip({ child, active, onClick }) {
  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-2xl border-2 transition-all shrink-0 ${
        active
          ? 'border-indigo-500 bg-indigo-50 shadow-sm'
          : 'border-slate-200 bg-white hover:border-indigo-300'
      }`}
    >
      <ChildAvatar name={child.name} size={34} />
      <div className="text-left min-w-0">
        <p className={`text-sm font-semibold font-sora leading-tight truncate max-w-[120px] ${active ? 'text-indigo-700' : 'text-slate-800'}`}>
          {child.name}
        </p>
        <p className="text-[11px] text-slate-400 leading-tight">Class {child.class} · {child.relation}</p>
      </div>
    </motion.button>
  );
}

// ── Tab bar ────────────────────────────────────────────────────────────────

function TabBar({ active, onChange }) {
  return (
    <div className="flex overflow-x-auto scrollbar-hide border-b border-slate-100">
      {TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = active === tab.id;
        const isHighlight = tab.highlight;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`flex flex-col sm:flex-row items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2.5 sm:py-3 text-[10px] sm:text-sm font-semibold whitespace-nowrap transition-all shrink-0 border-b-2 -mb-px ${
              isActive && isHighlight
                ? 'text-amber-600 border-amber-500 bg-amber-50/60'
                : isActive
                ? 'text-indigo-600 border-indigo-500'
                : isHighlight
                ? 'text-amber-500 border-transparent hover:text-amber-600 hover:bg-amber-50/40'
                : 'text-slate-400 border-transparent hover:text-slate-600'
            }`}
          >
            <Icon className="w-4 h-4 sm:w-3.5 sm:h-3.5" strokeWidth={2.5} />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Overview view ──────────────────────────────────────────────────────────

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-xs text-slate-400 w-24 sm:w-32 shrink-0 font-medium leading-relaxed">{label}</span>
      <span className="text-xs text-slate-700 font-semibold break-words min-w-0 leading-relaxed">{value}</span>
    </div>
  );
}

function OverviewView({ summary, loading }) {
  if (loading) return <Spinner />;
  if (!summary) return <Empty text="Could not load summary" />;

  const s   = summary.student;
  const att = summary.attendance;
  const attColor = att ? (att.percentage >= 75 ? 'green' : 'red') : 'slate';

  const fmtDob = s?.dob
    ? new Date(s.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const fmtAdmDate = s?.admissionDate
    ? new Date(s.admissionDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const father = s?.parents?.find(p => p.relation === 'Father');
  const mother = s?.parents?.find(p => p.relation === 'Mother');

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Attendance"
          value={att ? `${att.percentage}%` : '—'}
          sub={att?.month || 'This month'}
          icon={CheckSquare}
          color={attColor}
          badge={att && att.percentage < 75 ? 'Low' : undefined}
        />
        <StatCard
          label="Avg Marks"
          value={summary.avgMarksPercentage != null ? `${summary.avgMarksPercentage}%` : '—'}
          sub="Last 10 exams"
          icon={BarChart3}
          color={summary.avgMarksPercentage >= 60 ? 'indigo' : 'amber'}
        />
        <StatCard
          label="Pending Work"
          value={summary.pendingAssignments ?? '—'}
          sub="Assignments due"
          icon={Paperclip}
          color={summary.pendingAssignments > 0 ? 'amber' : 'green'}
          badge={summary.pendingAssignments > 0 ? 'Action' : undefined}
        />
        <StatCard
          label="Late Submissions"
          value={summary.lateAssignments ?? '—'}
          icon={Clock}
          color={summary.lateAssignments > 0 ? 'red' : 'green'}
        />
      </div>

      {/* Alerts */}
      {att && att.percentage < 75 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <p className="text-sm font-semibold text-red-700">Attendance below 75%</p>
            <p className="text-xs text-red-500 mt-0.5">
              Attendance is {att.percentage}% this month. Minimum 75% is required.
            </p>
          </div>
        </motion.div>
      )}
      {summary.pendingAssignments > 0 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-2xl">
          <Paperclip className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <p className="text-sm font-semibold text-amber-700">
              {summary.pendingAssignments} assignment{summary.pendingAssignments !== 1 ? 's' : ''} pending
            </p>
            <p className="text-xs text-amber-500 mt-0.5">Check the Assignments tab to see which are due.</p>
          </div>
        </motion.div>
      )}

      {/* Student profile details */}
      {s && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Academic info */}
          <SectionCard>
            <SectionHeader title="Academic Details" />
            <div className="px-4 sm:px-5 py-2 divide-y divide-slate-50">
              <DetailRow label="Admission No."  value={s.admissionNo} />
              <DetailRow label="Student Code"   value={s.studentCode} />
              <DetailRow label="Roll No."        value={s.rollNo} />
              <DetailRow label="Class"           value={s.section ? `${s.class} – ${s.section}` : s.class} />
              <DetailRow label="Stream"          value={s.stream} />
              <DetailRow label="Academic Year"   value={s.academicYear} />
              <DetailRow label="Admission Date"  value={fmtAdmDate} />
              <DetailRow label="Status"          value={s.status} />
              <DetailRow label="Fee Status"      value={s.feeStatus} />
            </div>
          </SectionCard>

          {/* Personal info */}
          <SectionCard>
            <SectionHeader title="Personal Details" />
            <div className="px-4 sm:px-5 py-2 divide-y divide-slate-50">
              <DetailRow label="Date of Birth"   value={fmtDob} />
              <DetailRow label="Gender"          value={s.gender} />
              <DetailRow label="Blood Group"     value={s.bloodGroup} />
              <DetailRow label="Nationality"     value={s.nationality} />
              <DetailRow label="Religion"        value={s.religion} />
              <DetailRow label="Phone"           value={s.phone} />
              <DetailRow label="Email"           value={s.email} />
              <DetailRow label="Address"         value={[s.address || s.currentAddress, s.city, s.state, s.pinCode].filter(Boolean).join(', ')} />
              <DetailRow label="Medical"         value={s.medicalConditions} />
              <DetailRow label="Transport"       value={s.transportRequired !== 'No' ? `${s.transportRequired}${s.busRoute ? ` · ${s.busRoute}` : ''}` : null} />
            </div>
          </SectionCard>

          {/* Parent info */}
          {(father || mother || s.guardianName) && (
            <SectionCard className="col-span-1 sm:col-span-2">
              <SectionHeader title="Parent / Guardian Details" />
              <div className="divide-y divide-slate-50">
                {[father, mother].filter(Boolean).map(p => (
                  <div key={p.relation} className="px-4 sm:px-5 py-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{p.relation}</span>
                      <span className="text-sm font-semibold text-slate-800">{p.name}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                      {p.phone && <DetailRow label="Phone" value={p.phone} />}
                      {p.email && <DetailRow label="Email" value={p.email} />}
                      {p.occupation && <DetailRow label="Occupation" value={p.occupation} />}
                    </div>
                  </div>
                ))}
                {s.guardianName && (
                  <div className="px-5 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">Guardian</span>
                      <span className="text-sm font-semibold text-slate-800">{s.guardianName}</span>
                    </div>
                    <DetailRow label="Phone" value={s.guardianPhone} />
                    <DetailRow label="Relation" value={s.guardianRelation} />
                  </div>
                )}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}

// ── Attendance view ────────────────────────────────────────────────────────

function AttendanceView({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/parents/my/children/${studentId}/attendance`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Spinner />;
  if (!data) return <Empty text="No attendance data available" icon={CheckSquare} />;

  const { months = [], overall } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Overall %"
          value={`${overall.percentage}%`}
          icon={CheckSquare}
          color={overall.percentage >= 75 ? 'green' : 'red'}
          badge={overall.percentage < 75 ? 'Low' : undefined}
        />
        <StatCard label="Present" value={overall.present} icon={TrendingUp}   color="green" />
        <StatCard label="Absent"  value={overall.absent}  icon={TrendingDown} color="red" />
        <StatCard label="Late"    value={overall.late}    icon={Clock}        color="amber" />
      </div>

      {months.length > 0 && (
        <SectionCard>
          <SectionHeader title="Monthly Breakdown" />
          <div className="divide-y divide-slate-50">
            {months.map(m => (
              <div key={m.month} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-20 shrink-0">
                  <p className="text-sm font-semibold text-slate-700 leading-tight">{m.month}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{m.present}P · {m.absent}A · {m.late}L</p>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${m.percentage}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      className={`h-full rounded-full ${pctBg(m.percentage)}`}
                    />
                  </div>
                  <span className={`text-sm font-bold w-10 text-right shrink-0 ${pctColor(m.percentage)}`}>
                    {m.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Grade chip helper ──────────────────────────────────────────────────────

function GradeChip({ percentage, grade }) {
  const cls =
    percentage >= 90 ? 'bg-emerald-100 text-emerald-700' :
    percentage >= 75 ? 'bg-indigo-100 text-indigo-700'   :
    percentage >= 60 ? 'bg-amber-100 text-amber-700'     :
    percentage >= 40 ? 'bg-orange-100 text-orange-700'   :
    'bg-red-100 text-red-700';
  return (
    <span className={`text-sm font-bold px-2.5 py-1 rounded-xl ${cls}`}>
      {grade || `${percentage}%`}
    </span>
  );
}

const TEST_TYPE_LABEL = {
  unit_test:   'Unit Test',
  half_yearly: 'Half Yearly',
  yearly:      'Yearly',
  class_test:  'Class Test',
  practical:   'Practical',
};

// ── Remarks renderer — handles plain text and markdown tables ─────────────

function RemarksBlock({ text }) {
  if (!text) return null;

  // Detect markdown table: lines that start with |
  const lines = text.trim().split('\n');
  const isTable = lines.length >= 2 && lines[0].trim().startsWith('|') && lines[1].trim().startsWith('|');

  if (isTable) {
    const headerCells = lines[0].split('|').map(c => c.trim()).filter(Boolean);
    const dataRows = lines.slice(2).filter(l => l.trim().startsWith('|'));
    return (
      <div className="mt-2 overflow-x-auto rounded-xl border border-slate-100">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50">
              {headerCells.map((h, i) => (
                <th key={i} className="px-3 py-2 text-left font-semibold text-slate-600 border-b border-slate-100">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => {
              const cells = row.split('|').map(c => c.trim()).filter(Boolean);
              return (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                  {cells.map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 text-slate-700 border-b border-slate-50">
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed whitespace-pre-line">{text}</p>
  );
}

// ── Marks view ─────────────────────────────────────────────────────────────

function MarksView({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null);

  useEffect(() => {
    setLoading(true);
    setActiveType(null);
    client.get(`/parents/my/children/${studentId}/marks`)
      .then(r => {
        setData(r.data);
        const types = Object.keys(r.data?.byType || {});
        if (types.length) setActiveType(types[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Spinner />;
  if (!data?.results?.length) return <Empty text="No exam results yet" icon={Award} />;

  const { byType = {}, subjectStats = [] } = data;
  const types = Object.keys(byType);

  return (
    <div className="space-y-4">

      {/* Subject performance */}
      {subjectStats.length > 0 && (
        <SectionCard>
          <SectionHeader title="Subject Performance" sub="Average across all tests" />
          {/* Mobile: stacked cards */}
          <div className="sm:hidden divide-y divide-slate-50">
            {[...subjectStats].sort((a, b) => b.avg - a.avg).map(s => (
              <div key={s.subject} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{s.subject}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${pctBg(s.avg)}`} style={{ width: `${s.avg}%` }} />
                    </div>
                    <span className={`text-xs font-bold shrink-0 ${pctColor(s.avg)}`}>{s.avg}%</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">Best {s.best}% · Low {s.worst}% · {s.count} tests</p>
                </div>
                <GradeChip percentage={s.avg} />
              </div>
            ))}
          </div>
          {/* Desktop: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Avg</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Best</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Lowest</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Tests</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Grade</th>
                </tr>
              </thead>
              <tbody>
                {[...subjectStats].sort((a, b) => b.avg - a.avg).map((s, i) => (
                  <tr key={s.subject} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                    <td className="px-5 py-3 font-semibold text-slate-800">{s.subject}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`text-xs font-bold ${pctColor(s.avg)}`}>{s.avg}%</span>
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pctBg(s.avg)}`} style={{ width: `${s.avg}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-xs font-semibold text-emerald-600">{s.best}%</td>
                    <td className="px-4 py-3 text-center text-xs font-semibold text-red-500">{s.worst}%</td>
                    <td className="px-4 py-3 text-center text-xs text-slate-400">{s.count}</td>
                    <td className="px-5 py-3 text-right"><GradeChip percentage={s.avg} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {/* Test type filter tabs */}
      {types.length > 0 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {types.map(t => (
            <button
              key={t}
              onClick={() => setActiveType(t)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                activeType === t
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {TEST_TYPE_LABEL[t] || t}
              <span className="ml-1.5 opacity-70">({byType[t].length})</span>
            </button>
          ))}
        </div>
      )}

      {/* Results for active test type */}
      {activeType && byType[activeType] && (
        <SectionCard>
          <SectionHeader
            title={TEST_TYPE_LABEL[activeType] || activeType}
            sub={`${byType[activeType].length} result${byType[activeType].length !== 1 ? 's' : ''}`}
          />
          {/* Mobile: stacked cards */}
          <div className="sm:hidden divide-y divide-slate-50">
            {byType[activeType].map(r => (
              <div key={r._id} className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{r.testTitle || r.subject}</p>
                  {r.testTitle && <p className="text-xs text-indigo-500 mt-0.5">{r.subject}</p>}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-400">
                    {r.testDate && <span>{fmtDate(r.testDate)}</span>}
                    {!r.absent && <span className="font-semibold text-slate-600">{r.marksObtained}/{r.maxMarks}</span>}
                    {r.absent && <span className="text-red-500 font-semibold">Absent</span>}
                  </div>
                  {r.remarks && <RemarksBlock text={r.remarks} />}
                </div>
                {!r.absent && <GradeChip percentage={r.percentage} grade={r.grade} />}
              </div>
            ))}
          </div>
          {/* Desktop: table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject / Test</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Marks</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Grade</th>
                </tr>
              </thead>
              <tbody>
                {byType[activeType].map((r, i) => (
                  <tr key={r._id} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-slate-800 leading-tight">{r.testTitle || r.subject}</p>
                      {r.testTitle && <p className="text-xs text-indigo-500 mt-0.5">{r.subject}</p>}
                      {r.remarks && <RemarksBlock text={r.remarks} />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.absent ? <span className="text-xs font-semibold text-red-500">Absent</span>
                        : <span className="text-xs font-bold text-slate-700">{r.marksObtained} / {r.maxMarks}</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-slate-400 whitespace-nowrap">{r.testDate ? fmtDate(r.testDate) : '—'}</td>
                    <td className="px-5 py-3 text-right">{!r.absent && <GradeChip percentage={r.percentage} grade={r.grade} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Assignments view ───────────────────────────────────────────────────────

const ASSIGNMENT_BADGE = {
  pending:   { label: 'Pending',        cls: 'bg-red-100 text-red-700' },
  late:      { label: 'Late Submitted', cls: 'bg-amber-100 text-amber-700' },
  submitted: { label: 'Submitted',      cls: 'bg-emerald-100 text-emerald-700' },
  upcoming:  { label: 'Upcoming',       cls: 'bg-slate-100 text-slate-500' },
};

function assignmentBadge(a) {
  if (a.isPending) return ASSIGNMENT_BADGE.pending;
  if (a.isLate)    return ASSIGNMENT_BADGE.late;
  if (a.submitted) return ASSIGNMENT_BADGE.submitted;
  return ASSIGNMENT_BADGE.upcoming;
}

function AssignmentsView({ studentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/parents/my/children/${studentId}/assignments`)
      .then(r => setItems(r.data.assignments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Spinner />;
  if (!items.length) return <Empty text="No assignments found" icon={Paperclip} />;

  const pending = items.filter(a => a.isPending);
  const rest    = items.filter(a => !a.isPending);

  const renderItem = (a) => {
    const b = assignmentBadge(a);
    return (
      <div key={a._id} className="px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
            <Paperclip className="w-3.5 h-3.5 text-indigo-400" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-slate-800 leading-tight">{a.title}</p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 leading-snug ${b.cls}`}>
                {b.label}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-400">
              <span>{a.subject}</span>
              {a.type && <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-md font-medium">{a.type}</span>}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" strokeWidth={2} /> Due {fmtDate(a.dueDate)}
              </span>
            </div>

            {/* Marks / grading row */}
            {a.graded && a.totalScore != null ? (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-1.5 w-fit">
                  <span className="text-xs text-slate-500">Score</span>
                  <span className="text-sm font-bold text-slate-800">
                    {a.totalScore} / {a.maxScore}
                  </span>
                  {a.scorePct != null && (
                    <GradeChip percentage={a.scorePct} />
                  )}
                </div>
                {a.feedback && (
                  <div className="border-l-2 border-indigo-200 pl-3">
                    <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wide mb-1">Teacher Feedback</p>
                    <RemarksBlock text={a.feedback} />
                  </div>
                )}
              </div>
            ) : a.submitted && !a.graded ? (
              <p className="mt-1.5 text-[11px] text-indigo-500 font-medium">Submitted · Awaiting grading</p>
            ) : null}

            {/* Rubric breakdown */}
            {a.graded && a.grades?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {a.grades.map((g, i) => (
                  <span key={i} className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-semibold">
                    {g.criterion}: {g.score}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {pending.length > 0 && (
        <SectionCard>
          <SectionHeader title="Pending" sub={`${pending.length} assignment${pending.length !== 1 ? 's' : ''} overdue`} />
          <div className="divide-y divide-slate-50">{pending.map(renderItem)}</div>
        </SectionCard>
      )}
      {rest.length > 0 && (
        <SectionCard>
          <SectionHeader title="All Assignments" />
          <div className="divide-y divide-slate-50">{rest.map(renderItem)}</div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Homework view ──────────────────────────────────────────────────────────

function HomeworkView({ studentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/parents/my/children/${studentId}/homework`)
      .then(r => setItems(r.data.homework || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Spinner />;
  if (!items.length) return <Empty text="No homework found" icon={PenTool} />;

  return (
    <SectionCard>
      <SectionHeader title="Homework" sub={`${items.length} item${items.length !== 1 ? 's' : ''}`} />
      <div className="divide-y divide-slate-50">
        {items.map(h => (
          <div key={h._id} className="px-5 py-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center shrink-0 mt-0.5">
              <PenTool className="w-3.5 h-3.5 text-violet-400" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 leading-tight">{h.title}</p>
              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                <span>{h.subject}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" strokeWidth={2} /> Due {fmtDate(h.dueDate)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ── Announcements view ─────────────────────────────────────────────────────

const PRIORITY_BADGE = {
  urgent:    { cls: 'bg-red-100 text-red-700',     label: 'Urgent' },
  important: { cls: 'bg-amber-100 text-amber-700', label: 'Important' },
  normal:    { cls: 'bg-slate-100 text-slate-500', label: 'Normal' },
};

function AnnouncementsView() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [ackingId, setAckingId] = useState(null);

  const savedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('edupulse_user') || 'null'); } catch { return null; }
  }, []);
  const myUserId = savedUser?._id || savedUser?.id || '';

  useEffect(() => {
    setLoading(true);
    client.get('/announcements?limit=50')
      .then(r => setItems(r.data.announcements || r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAck = async (announcementId) => {
    setAckingId(announcementId);
    try {
      await client.patch(`/announcements/${announcementId}/acknowledge`);
      setItems(prev => prev.map(a =>
        a._id === announcementId
          ? { ...a, acknowledgedBy: [...(a.acknowledgedBy || []), myUserId] }
          : a
      ));
    } catch { /* toast shown by client */ }
    finally { setAckingId(null); }
  };

  if (loading) return <Spinner />;
  if (!items.length) return <Empty text="No announcements" icon={Megaphone} />;

  // Sort: unacknowledged requiresAck items first
  const sorted = [...items].sort((a, b) => {
    const aAcked = a.requiresAck && (a.acknowledgedBy || []).some(id => id.toString() === myUserId.toString());
    const bAcked = b.requiresAck && (b.acknowledgedBy || []).some(id => id.toString() === myUserId.toString());
    if (a.requiresAck && !aAcked && !(b.requiresAck && !bAcked)) return -1;
    if (b.requiresAck && !bAcked && !(a.requiresAck && !aAcked)) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const pendingAck = sorted.filter(a => a.requiresAck && !(a.acknowledgedBy || []).some(id => id.toString() === myUserId.toString())).length;

  return (
    <div className="space-y-3">
      {pendingAck > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          <AlertCircle size={15} className="text-amber-500 shrink-0" />
          <p className="text-xs font-medium text-amber-700">
            {pendingAck} announcement{pendingAck > 1 ? 's' : ''} require{pendingAck === 1 ? 's' : ''} your acknowledgment
          </p>
        </div>
      )}

      {sorted.map(a => {
        const badge   = PRIORITY_BADGE[a.priority] || PRIORITY_BADGE.normal;
        const isAcked = (a.acknowledgedBy || []).some(id => id.toString() === myUserId.toString());
        const needsAck = a.requiresAck && !isAcked;
        const acking  = ackingId === a._id;

        return (
          <SectionCard key={a._id}>
            <div className={`p-4 sm:p-5 ${needsAck ? 'border-l-4 border-amber-400' : ''}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {a.pinned && (
                    <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                      <Pin className="w-2.5 h-2.5" strokeWidth={2.5} /> Pinned
                    </span>
                  )}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>
                    {badge.label}
                  </span>
                  {a.requiresAck && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                      isAcked ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {isAcked ? '✓ Acknowledged' : '⚠ Ack. Required'}
                    </span>
                  )}
                </div>
                <span className="text-[11px] text-slate-400 shrink-0">
                  {new Date(a.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>

              <h3 className="font-bold text-slate-800 text-sm leading-snug mb-1.5">{a.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{a.body}</p>

              {a.createdBy?.name && (
                <p className="text-[11px] text-slate-400 mt-2">— {a.createdBy.name}</p>
              )}

              {needsAck && (
                <div className="mt-3 pt-3 border-t border-amber-100">
                  <button
                    onClick={() => handleAck(a._id)}
                    disabled={acking}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-xs font-bold rounded-lg transition-colors"
                  >
                    {acking
                      ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <CheckSquare size={13} />
                    }
                    {acking ? 'Confirming…' : 'I have reviewed this — Acknowledge'}
                  </button>
                </div>
              )}

              {isAcked && a.requiresAck && (
                <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-600">
                  <CheckSquare size={12} />
                  You acknowledged this message
                </div>
              )}
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
}

// ── Materials view ─────────────────────────────────────────────────────────

const MATERIAL_TYPE_BADGE = {
  pdf:      { cls: 'bg-red-50 text-red-600',       label: 'PDF' },
  video:    { cls: 'bg-violet-50 text-violet-600',  label: 'Video' },
  link:     { cls: 'bg-blue-50 text-blue-600',      label: 'Link' },
  notes:    { cls: 'bg-emerald-50 text-emerald-600', label: 'Notes' },
  exercise: { cls: 'bg-amber-50 text-amber-600',    label: 'Exercise' },
};

function MaterialsView({ child }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!child) return;
    setLoading(true);
    client.get(`/materials?class=${encodeURIComponent(child.class)}&limit=100`)
      .then(r => setItems(Array.isArray(r.data) ? r.data : r.data.materials || r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [child]);

  if (loading) return <Spinner />;
  if (!items.length) return <Empty text="No materials uploaded yet" icon={BookOpen} />;

  const bySubject = items.reduce((acc, m) => {
    const key = m.subject || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {Object.entries(bySubject).map(([subject, mats]) => (
        <SectionCard key={subject}>
          <SectionHeader title={subject} sub={`${mats.length} item${mats.length !== 1 ? 's' : ''}`} />
          <div className="divide-y divide-slate-50">
            {mats.map(m => {
              const typeKey = (m.type || '').toLowerCase();
              const badge = MATERIAL_TYPE_BADGE[typeKey] || { cls: 'bg-slate-50 text-slate-500', label: m.type || 'File' };
              const href = m.fileUrl || m.link;
              return (
                <div key={m._id} className="px-5 py-4 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5">
                    <FileText className="w-3.5 h-3.5 text-indigo-400" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800 leading-tight">{m.title}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${badge.cls}`}>{badge.label}</span>
                    </div>
                    {m.chapter && <p className="text-xs text-slate-400 mt-0.5">{m.chapter}</p>}
                    {m.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{m.description}</p>}
                    {href && (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        Open material →
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ))}
    </div>
  );
}

// ── Tests view ─────────────────────────────────────────────────────────────

const TEST_STATUS_BADGE = {
  upcoming:  { label: 'Upcoming',  cls: 'bg-indigo-100 text-indigo-700' },
  today:     { label: 'Today',     cls: 'bg-amber-100  text-amber-700'  },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
};

const TEST_TYPE_MAP = {
  unit_test:   'Unit Test',
  half_yearly: 'Half Yearly',
  yearly:      'Yearly',
  class_test:  'Class Test',
  practical:   'Practical',
};

function TestsView({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    setLoading(true);
    setActiveType('all');
    client.get(`/parents/my/children/${studentId}/tests`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Spinner />;
  if (!data?.tests?.length) return <Empty text="No tests scheduled yet" icon={FlaskConical} />;

  const { tests, summary, byType = {} } = data;
  const types = Object.keys(byType);

  const displayed = activeType === 'all' ? tests : (byType[activeType] || []);

  return (
    <div className="space-y-4">

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Tests"   value={summary.total}     icon={FlaskConical} color="indigo" />
        <StatCard label="Attempted"     value={summary.attempted} icon={CheckSquare}  color="green" />
        <StatCard label="Avg Score"     value={summary.avgPct != null ? `${summary.avgPct}%` : '—'} icon={BarChart3}
          color={summary.avgPct >= 75 ? 'green' : summary.avgPct >= 50 ? 'amber' : 'red'} />
        <StatCard label="Absent"        value={summary.absent}    icon={AlertCircle}  color={summary.absent > 0 ? 'red' : 'green'} />
      </div>

      {/* Best / Worst */}
      {summary.best != null && (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
            <TrendingUp className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
            <div>
              <p className="text-xs text-emerald-600 font-semibold">Best Score</p>
              <p className="text-xl font-extrabold text-emerald-700 font-sora">{summary.best}%</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-2xl px-4 py-3">
            <TrendingDown className="w-5 h-5 text-red-400 shrink-0" strokeWidth={2.5} />
            <div>
              <p className="text-xs text-red-500 font-semibold">Lowest Score</p>
              <p className="text-xl font-extrabold text-red-600 font-sora">{summary.worst}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Type filter */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        <button
          onClick={() => setActiveType('all')}
          className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
            activeType === 'all' ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          All ({tests.length})
        </button>
        {types.map(t => (
          <button
            key={t}
            onClick={() => setActiveType(t)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              activeType === t ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {TEST_TYPE_MAP[t] || t} ({byType[t].length})
          </button>
        ))}
      </div>

      {/* Tests list */}
      <SectionCard>
        {/* Mobile: cards */}
        <div className="sm:hidden divide-y divide-slate-50">
          {displayed.map(t => {
            const statusBadge = TEST_STATUS_BADGE[t.status] || TEST_STATUS_BADGE.upcoming;
            return (
              <div key={t._id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 leading-tight">{t.title}</p>
                    <p className="text-xs text-indigo-500 mt-0.5">{t.subject}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${statusBadge.cls}`}>
                    {statusBadge.label}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
                  <span>{TEST_TYPE_MAP[t.type] || t.type}</span>
                  {t.date && <span>{fmtDate(t.date)}</span>}
                  {t.attempted && !t.absent && <span className="font-bold text-slate-700">{t.marksObtained}/{t.maxMarks}</span>}
                  {t.absent && <span className="text-red-500 font-semibold">Absent</span>}
                  {t.createdByName && <span>by {t.createdByName}</span>}
                </div>
                {t.attempted && !t.absent && t.percentage != null && (
                  <div className="mt-2"><GradeChip percentage={t.percentage} grade={t.grade} /></div>
                )}
                {t.remarks && <RemarksBlock text={t.remarks} />}
              </div>
            );
          })}
        </div>
        {/* Desktop: table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Test</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Marks</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Grade</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((t, i) => {
                const statusBadge = TEST_STATUS_BADGE[t.status] || TEST_STATUS_BADGE.upcoming;
                return (
                  <tr key={t._id} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-slate-800 leading-tight">{t.title}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{TEST_TYPE_MAP[t.type] || t.type}{t.createdByName ? ` · ${t.createdByName}` : ''}</p>
                      {t.remarks && <RemarksBlock text={t.remarks} />}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 font-medium">{t.subject}</td>
                    <td className="px-4 py-3 text-center text-xs text-slate-500 whitespace-nowrap">{t.date ? fmtDate(t.date) : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {!t.attempted ? <span className="text-xs text-slate-300">—</span>
                        : t.absent ? <span className="text-xs font-semibold text-red-500">Absent</span>
                        : <span className="text-xs font-bold text-slate-700">{t.marksObtained} / {t.maxMarks}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {t.attempted && !t.absent && t.percentage != null
                        ? <GradeChip percentage={t.percentage} grade={t.grade} />
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadge.cls}`}>{statusBadge.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Premium view ───────────────────────────────────────────────────────────

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '₹249',
    period: '/month',
    icon: Zap,
    color: 'indigo',
    badge: null,
    tagline: 'Essential tracking for every parent',
    features: [
      'Real-time attendance alerts',
      'Monthly attendance reports',
      'Test & exam results',
      'Assignment status tracking',
      'Homework notifications',
      'School announcements',
      'Study materials access',
    ],
    cta: 'Get Started',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹449',
    period: '/month',
    icon: Star,
    color: 'violet',
    badge: 'Most Popular',
    tagline: 'Deep insights into your child\'s progress',
    features: [
      'Everything in Starter',
      'Detailed performance analytics',
      'Subject-wise progress charts',
      'Assignment marks & feedback',
      'Teacher chat history (read)',
      'Weekly progress digest email',
      'Late submission alerts (instant)',
      'Fee payment reminders',
    ],
    cta: 'Upgrade to Pro',
  },
  {
    id: 'elite',
    name: 'Elite',
    price: '₹999',
    period: '/month',
    icon: Crown,
    color: 'amber',
    badge: 'Best Value',
    tagline: 'Complete visibility & AI-powered guidance',
    features: [
      'Everything in Pro',
      'AI-powered performance summary',
      'Predicted grade trend alerts',
      'Comparative class ranking',
      'Direct teacher messaging',
      'Monthly parent-teacher report',
      'Priority school admin support',
      'Multi-child management',
      'Annual academic report PDF',
    ],
    cta: 'Go Elite',
  },
];

const PLAN_STYLES = {
  indigo: {
    card:    'border-indigo-100 bg-white',
    header:  'bg-indigo-50',
    icon:    'bg-indigo-100 text-indigo-600',
    badge:   'bg-indigo-100 text-indigo-700',
    price:   'text-indigo-700',
    check:   'text-indigo-500',
    btn:     'bg-indigo-600 hover:bg-indigo-700 text-white',
  },
  violet: {
    card:    'border-violet-200 bg-white shadow-lg shadow-violet-100/60',
    header:  'bg-violet-600',
    icon:    'bg-white/20 text-white',
    badge:   'bg-white text-violet-700',
    price:   'text-white',
    check:   'text-violet-200',
    btn:     'bg-white text-violet-700 hover:bg-violet-50',
    popular: true,
  },
  amber: {
    card:    'border-amber-200 bg-white',
    header:  'bg-gradient-to-br from-amber-500 to-orange-500',
    icon:    'bg-white/20 text-white',
    badge:   'bg-white text-amber-700',
    price:   'text-white',
    check:   'text-amber-200',
    btn:     'bg-white text-amber-700 hover:bg-amber-50',
  },
};

// ── Coaching Feedback View (parent) ───────────────────────────────────────

const BEHAVIOR_LABELS = {
  5: { label: 'Excellent',       cls: 'bg-emerald-100 text-emerald-700' },
  4: { label: 'Good',            cls: 'bg-indigo-100  text-indigo-700'  },
  3: { label: 'Average',         cls: 'bg-amber-100   text-amber-700'   },
  2: { label: 'Needs Attention', cls: 'bg-rose-100    text-rose-700'    },
  1: { label: 'Absent',          cls: 'bg-slate-100   text-slate-500'   },
};

const HW_LABELS = {
  done:     { label: 'Done',     cls: 'bg-emerald-100 text-emerald-700' },
  partial:  { label: 'Partial',  cls: 'bg-amber-100   text-amber-700'   },
  not_done: { label: 'Not Done', cls: 'bg-rose-100    text-rose-700'    },
  na:       { label: 'N/A',      cls: 'bg-slate-100   text-slate-500'   },
};

function CoachingView({ studentId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSubject, setFilterSubject] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    client.get(`/parents/my/children/${studentId}/coaching`)
      .then(r => setEntries(r.data.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  const subjects = useMemo(() => [...new Set(entries.map(e => e.subject).filter(Boolean))], [entries]);

  const filtered = useMemo(() =>
    filterSubject ? entries.filter(e => e.subject === filterSubject) : entries,
    [entries, filterSubject]
  );

  // Group by date+class+subject session
  const groups = useMemo(() => {
    const map = {};
    filtered.forEach(e => {
      const key = `${e.date?.slice(0,10)}_${e.class}_${e.subject}_${e.sessionTitle||''}`;
      if (!map[key]) map[key] = { date: e.date, class: e.class, subject: e.subject, sessionTitle: e.sessionTitle, teacherName: e.teacherName, entries: [] };
      map[key].entries.push(e);
    });
    return Object.values(map).sort((a,b) => new Date(b.date) - new Date(a.date));
  }, [filtered]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-800 text-base">Class Feedback</h3>
          <p className="text-xs text-slate-400 mt-0.5">Observations from teachers after class</p>
        </div>
        {subjects.length > 0 && (
          <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-300 bg-white">
            <option value="">All Subjects</option>
            {subjects.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      {groups.length === 0 ? (
        <Empty text="No coaching feedback yet" icon={Star} />
      ) : (
        <div className="space-y-3">
          {groups.map((g, gi) => (
            <SectionCard key={gi}>
              {/* Session header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-50 bg-slate-50/60">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm">
                      {g.sessionTitle || `${g.subject} – ${g.class}`}
                    </span>
                    <span className="text-[10px] bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{g.subject}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Class {g.class}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Calendar size={10} /> {fmtDate(g.date)}
                    </span>
                    {g.teacherName && (
                      <span className="text-[11px] text-slate-400">by {g.teacherName}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Per-student rows */}
              {g.entries.map(e => {
                const expanded = expandedId === e._id;
                const behCfg = BEHAVIOR_LABELS[e.behaviorRating];
                const hwCfg  = HW_LABELS[e.homeworkStatus] || HW_LABELS.na;
                return (
                  <div key={e._id} className="border-b border-slate-50 last:border-0">
                    <div className="px-4 py-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : e._id)}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          {/* ratings row */}
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {behCfg && (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${behCfg.cls}`}>
                                {behCfg.label}
                              </span>
                            )}
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${hwCfg.cls}`}>
                              HW: {hwCfg.label}
                            </span>
                            {e.participationRating > 0 && (
                              <span className="text-[11px] text-amber-500 font-bold tracking-tight">
                                {'★'.repeat(e.participationRating)}{'☆'.repeat(5-e.participationRating)}
                              </span>
                            )}
                          </div>
                          {e.feedback && !expanded && (
                            <p className="text-xs text-slate-500 mt-1.5 italic truncate">"{e.feedback}"</p>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 mt-0.5 shrink-0">
                          {expanded ? '▲' : '▼'}
                        </span>
                      </div>
                    </div>

                    <AnimatePresence>
                      {expanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className="overflow-hidden">
                          <div className="px-4 pb-4 space-y-3">
                            {e.topicsCovered && (
                              <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Topics Covered</p>
                                <p className="text-sm text-slate-700 whitespace-pre-line">{e.topicsCovered}</p>
                              </div>
                            )}
                            {e.feedback && (
                              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
                                <p className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide mb-1">Teacher's Feedback</p>
                                <p className="text-sm text-slate-700 whitespace-pre-line">{e.feedback}</p>
                              </div>
                            )}
                            <div className="flex gap-4 flex-wrap">
                              {e.behaviorRating && (
                                <div>
                                  <p className="text-[10px] text-slate-400 mb-1">Behavior</p>
                                  <div className="flex gap-0.5">
                                    {[1,2,3,4,5].map(n => (
                                      <Star key={n} size={13}
                                        className={n <= e.behaviorRating ? "text-amber-400" : "text-slate-200"}
                                        fill={n <= e.behaviorRating ? "currentColor" : "none"} />
                                    ))}
                                  </div>
                                </div>
                              )}
                              {e.participationRating > 0 && (
                                <div>
                                  <p className="text-[10px] text-slate-400 mb-1">Participation</p>
                                  <div className="flex gap-0.5">
                                    {[1,2,3,4,5].map(n => (
                                      <Star key={n} size={13}
                                        className={n <= e.participationRating ? "text-indigo-400" : "text-slate-200"}
                                        fill={n <= e.participationRating ? "currentColor" : "none"} />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </SectionCard>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Razorpay script loader ────────────────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise(resolve => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const PLAN_AMOUNTS = { starter: 249, pro: 449, elite: 999 };

function PremiumView({ user }) {
  const [selected,     setSelected]     = useState(null);
  const [paying,       setPaying]       = useState(false);
  const [subscription, setSubscription] = useState(undefined); // undefined = loading
  const [successPlan,  setSuccessPlan]  = useState(null);

  // Load current subscription status
  useEffect(() => {
    client.get('/payments/my-subscription')
      .then(r => setSubscription(r.data.subscription))
      .catch(() => setSubscription(null));
  }, []);

  const activePlan = subscription?.plan;
  const expiresAt  = subscription?.expiresAt;

  const handlePay = async () => {
    if (!selected || paying) return;
    setPaying(true);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { alert('Failed to load payment gateway. Check your internet.'); setPaying(false); return; }

      const order = await client.post('/payments/create-order', { plan: selected }).then(r => r.data);

      const options = {
        key:         order.keyId,
        amount:      order.amount,
        currency:    order.currency,
        name:        'EduPulse AI',
        description: `${order.planName} — Monthly Subscription`,
        order_id:    order.orderId,
        prefill: {
          name:  user?.name  || '',
          email: user?.email || '',
          contact: user?.phone || '',
        },
        theme: { color: '#4F46E5' },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (response) => {
          try {
            await client.post('/payments/verify', {
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setSuccessPlan(selected);
            setSelected(null);
            // refresh subscription
            const r = await client.get('/payments/my-subscription');
            setSubscription(r.data.subscription);
          } catch {
            /* toast shown by client interceptor */
          } finally {
            setPaying(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { setPaying(false); });
      rzp.open();
    } catch {
      setPaying(false);
    }
  };

  // Active subscription banner
  if (subscription === undefined) return <Spinner />;

  return (
    <div className="space-y-5">

      {/* Success flash */}
      <AnimatePresence>
        {successPlan && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-center gap-4"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} />
            </div>
            <div>
              <p className="font-bold text-emerald-800 text-sm">Payment Successful! 🎉</p>
              <p className="text-xs text-emerald-600 mt-0.5">
                Your {PLANS.find(p => p.id === successPlan)?.name} plan is now active.
              </p>
            </div>
            <button onClick={() => setSuccessPlan(null)} className="ml-auto text-emerald-400 hover:text-emerald-600">✕</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active subscription card */}
      {activePlan && (
        <div className="bg-gradient-to-r from-emerald-50 to-indigo-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-800 text-sm">
              {PLANS.find(p => p.id === activePlan)?.name} Plan — Active
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Valid until {fmtDate(expiresAt)} · Auto-renewal off
            </p>
          </div>
          <span className="shrink-0 text-[11px] font-bold bg-emerald-600 text-white px-2.5 py-1 rounded-full">Active</span>
        </div>
      )}

      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-indigo-600 to-indigo-800 px-5 py-6 sm:px-8 sm:py-8 text-white">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-amber-300" strokeWidth={2.5} />
            <span className="text-xs font-bold uppercase tracking-widest text-indigo-200">EduPulse Premium</span>
          </div>
          <h2 className="font-bold text-xl sm:text-2xl leading-tight mb-2">
            Stay closer to your child's learning
          </h2>
          <p className="text-sm text-indigo-200 max-w-md leading-relaxed">
            Real-time updates, detailed reports, and AI-powered insights to support your child's academic journey.
          </p>
        </div>
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -bottom-10 -right-4 w-28 h-28 rounded-full bg-white/5" />
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {PLANS.map(plan => {
          const s = PLAN_STYLES[plan.color];
          const Icon = plan.icon;
          const isSelected = selected === plan.id;
          const isCurrent  = activePlan === plan.id;

          return (
            <motion.div
              key={plan.id}
              whileTap={{ scale: 0.98 }}
              className={`rounded-2xl border-2 overflow-hidden transition-all ${s.card} ${
                isSelected ? 'ring-2 ring-offset-2 ring-indigo-400' : ''
              } ${s.popular ? 'sm:-mt-2 sm:mb-2' : ''}`}
            >
              {/* Header */}
              <div className={`${s.header} px-5 py-4`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.icon}`}>
                    <Icon className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  {isCurrent ? (
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-500 text-white">Current Plan</span>
                  ) : plan.badge ? (
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${s.badge}`}>{plan.badge}</span>
                  ) : null}
                </div>
                <p className={`font-bold text-lg leading-none ${s.popular || plan.color === 'amber' ? 'text-white' : 'text-slate-800'}`}>
                  {plan.name}
                </p>
                <p className={`text-xs mt-1 ${s.popular || plan.color === 'amber' ? 'text-white/80' : 'text-slate-400'}`}>
                  {plan.tagline}
                </p>
                <div className="flex items-end gap-1 mt-3">
                  <span className={`font-bold text-3xl leading-none ${s.price}`}>{plan.price}</span>
                  <span className={`text-xs mb-1 ${s.popular || plan.color === 'amber' ? 'text-white/70' : 'text-slate-400'}`}>
                    {plan.period}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="px-5 py-4 space-y-2.5">
                {plan.features.map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-2.5 h-2.5 text-emerald-600" strokeWidth={3} />
                    </div>
                    <span className="text-xs text-slate-600 leading-relaxed">{f}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="px-5 pb-5">
                <button
                  disabled={isCurrent}
                  onClick={() => setSelected(isSelected ? null : plan.id)}
                  className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${s.btn} ${isSelected ? 'opacity-90' : ''}`}
                >
                  {isCurrent ? '✓ Active Plan' : isSelected ? '✓ Selected' : plan.cta}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Proceed to payment banner */}
      <AnimatePresence>
        {selected && !activePlan && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-indigo-50 border border-indigo-100 rounded-2xl px-5 py-4"
          >
            <div>
              <p className="text-sm font-bold text-indigo-800">
                {PLANS.find(p => p.id === selected)?.name} — ₹{PLAN_AMOUNTS[selected]}/month
              </p>
              <p className="text-xs text-indigo-500 mt-0.5">Secure checkout via Razorpay · 256-bit SSL</p>
            </div>
            <button
              onClick={handlePay}
              disabled={paying}
              className="shrink-0 px-5 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-60 transition-colors flex items-center gap-2"
            >
              {paying && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {paying ? 'Opening payment…' : 'Proceed to Payment →'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trust footer */}
      <div className="text-center text-xs text-slate-400 pb-2 space-y-1">
        <p>Secure payment powered by <span className="font-semibold text-slate-500">Razorpay</span> · 256-bit SSL encrypted</p>
        <p>Cancel anytime · No hidden charges</p>
      </div>
    </div>
  );
}

// ── Feedback view ─────────────────────────────────────────────────────────

const BEHAVIOR_OPTS = [
  { v: 5, label: 'Excellent',       cls: 'bg-emerald-100 text-emerald-700' },
  { v: 4, label: 'Good',            cls: 'bg-indigo-100  text-indigo-700'  },
  { v: 3, label: 'Average',         cls: 'bg-amber-100   text-amber-700'   },
  { v: 2, label: 'Needs Attention', cls: 'bg-orange-100  text-orange-700'  },
  { v: 1, label: 'Absent',          cls: 'bg-slate-100   text-slate-500'   },
];

const HW_OPTS_PARENT = [
  { v: 'done',     label: 'Done',     cls: 'bg-emerald-100 text-emerald-700' },
  { v: 'partial',  label: 'Partial',  cls: 'bg-amber-100   text-amber-700'   },
  { v: 'not_done', label: 'Not Done', cls: 'bg-red-100     text-red-700'     },
  { v: 'na',       label: 'N/A',      cls: 'bg-slate-100   text-slate-500'   },
];

function FeedbackView({ studentId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/parents/my/children/${studentId}/coaching`)
      .then(r => setEntries(r.data.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Spinner />;
  if (!entries.length) return <Empty text="No class feedback yet from teachers" icon={Star} />;

  return (
    <div className="space-y-3">
      {entries.map(e => {
        const behavior = BEHAVIOR_OPTS.find(o => o.v === e.behaviorRating);
        const hw       = HW_OPTS_PARENT.find(o => o.v === e.homeworkStatus);
        return (
          <SectionCard key={e._id}>
            <div className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-sora font-bold text-slate-800 text-sm leading-tight">
                    {e.sessionTitle || `${e.subject} Session`}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                    <span>{e.teacherName || 'Teacher'}</span>
                    <span>·</span>
                    <span>{e.subject}</span>
                  </div>
                </div>
                <span className="text-[11px] text-slate-400 shrink-0">
                  {fmtDate(e.date)}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {behavior && (
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-xl ${behavior.cls}`}>
                    Behaviour: {behavior.label}
                  </span>
                )}
                {hw && hw.v !== 'na' && (
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-xl ${hw.cls}`}>
                    Homework: {hw.label}
                  </span>
                )}
                {e.participationRating > 0 && (
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-xl bg-amber-50 text-amber-600">
                    Participation: {'★'.repeat(e.participationRating)}{'☆'.repeat(5 - e.participationRating)}
                  </span>
                )}
              </div>

              {e.topicsCovered && (
                <div className="text-xs text-slate-600 mb-2">
                  <span className="font-semibold text-slate-500">Topic: </span>{e.topicsCovered}
                </div>
              )}

              {e.feedback && (
                <div className="mt-2 px-3 py-2 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-1">Teacher's Note</p>
                  <p className="text-sm text-slate-700 leading-relaxed">"{e.feedback}"</p>
                </div>
              )}
            </div>
          </SectionCard>
        );
      })}
    </div>
  );
}

// ── Chat helpers ───────────────────────────────────────────────────────────

function avatarLetters(name = '') {
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}

function fmtMsgTime(iso) {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function fmtMsgDay(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Chat view ──────────────────────────────────────────────────────────────

function ChatView({ studentId }) {
  const [teachers, setTeachers]         = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [selected, setSelected]         = useState(null);
  const [chat, setChat]                 = useState(null);
  const [loadingChat, setLoadingChat]   = useState(false);
  const messagesEndRef                  = useRef(null);

  useEffect(() => {
    setLoadingTeachers(true);
    setSelected(null);
    setChat(null);
    client.get(`/parents/my/children/${studentId}/chat`)
      .then(r => setTeachers(r.data.teachers || []))
      .catch(() => {})
      .finally(() => setLoadingTeachers(false));
  }, [studentId]);

  useEffect(() => {
    if (!selected) return;
    const eid = selected.employeeId || selected._id;
    if (!eid) return;
    setLoadingChat(true);
    setChat(null);
    client.get(`/parents/my/children/${studentId}/chat/${eid}`)
      .then(r => setChat(r.data))
      .catch(() => {})
      .finally(() => setLoadingChat(false));
  }, [selected, studentId]);

  // Scroll to bottom whenever messages load
  useEffect(() => {
    if (chat) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }
  }, [chat]);

  if (loadingTeachers) return <Spinner />;
  if (!teachers.length) return <Empty text="No teachers assigned to this class yet" icon={MessageSquare} />;

  // ── Teacher list ──────────────────────────────────────────────────────────
  if (!selected) {
    return (
      <div className="bg-white rounded-2xl overflow-hidden border border-slate-100">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Teachers</p>
        </div>
        <div className="divide-y divide-slate-50">
          {teachers.map(t => {
            const key = t.employeeId || t._id;
            const letters = avatarLetters(t.name);
            return (
              <button
                key={key}
                onClick={() => setSelected(t)}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left"
              >
                {/* Avatar */}
                <div className="relative shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {letters}
                  </div>
                  {t.isClassTeacher && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                      <Check className="w-2 h-2 text-white" strokeWidth={3} />
                    </span>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{t.name}</p>
                    {t.lastAt && (
                      <p className="text-[10px] text-slate-400 shrink-0">{fmtMsgTime(t.lastAt)}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <p className="text-xs text-slate-400 truncate">
                      {t.lastMessage || (t.subjects?.join(', ') || t.designation || 'Teacher')}
                    </p>
                    {t.isClassTeacher && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 shrink-0">Class Teacher</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Conversation panel ────────────────────────────────────────────────────
  // Build messages with date separators
  const messages = chat?.messages || [];
  const grouped = [];
  let lastDay = null;
  messages.forEach(msg => {
    const day = new Date(msg.createdAt).toDateString();
    if (day !== lastDay) {
      grouped.push({ type: 'separator', label: fmtMsgDay(msg.createdAt), key: `sep-${day}` });
      lastDay = day;
    }
    grouped.push({ type: 'msg', msg, key: msg._id });
  });

  return (
    // Fixed full-screen overlay on mobile; inline on desktop
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#F0F0F0] lg:static lg:z-auto lg:rounded-2xl lg:overflow-hidden lg:border lg:border-slate-100">

      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-3 bg-indigo-600 text-white shrink-0 safe-top">
        <button
          onClick={() => setSelected(null)}
          className="p-1.5 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors shrink-0"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        </button>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold shrink-0">
          {avatarLetters(selected.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight truncate">{selected.name}</p>
          <p className="text-[11px] text-indigo-200 truncate">
            {selected.subjects?.join(', ') || selected.designation || 'Teacher'}
            {selected.isClassTeacher ? ' · Class Teacher' : ''}
          </p>
        </div>
        <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-white/15 text-white shrink-0">
          View only
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1" style={{ overscrollBehavior: 'contain' }}>
        {/* Read-only notice */}
        <div className="flex justify-center mb-2">
          <span className="text-[10px] text-slate-500 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm border border-slate-100">
            You can view this conversation but cannot send messages
          </span>
        </div>

        {loadingChat && <Spinner />}

        {!loadingChat && messages.length === 0 && (
          <Empty text="No messages yet" icon={MessageSquare} />
        )}

        {!loadingChat && grouped.map(item => {
          if (item.type === 'separator') {
            return (
              <div key={item.key} className="flex justify-center py-2">
                <span className="text-[10px] text-slate-500 bg-white/80 px-3 py-0.5 rounded-full shadow-sm border border-slate-100">
                  {item.label}
                </span>
              </div>
            );
          }
          const { msg } = item;
          const isStudent = msg.senderId?.toString() === studentId?.toString();
          return (
            <div key={item.key} className={`flex ${isStudent ? 'justify-end' : 'justify-start'}`}>
              <div className={`relative max-w-[78%] px-3.5 py-2 shadow-sm ${
                isStudent
                  ? 'bg-indigo-500 text-white rounded-2xl rounded-br-sm'
                  : 'bg-white text-slate-800 rounded-2xl rounded-bl-sm'
              }`}>
                {!isStudent && (
                  <p className="text-[10px] font-bold text-indigo-500 mb-0.5">{chat.teacher?.name}</p>
                )}
                <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                <p className={`text-[10px] mt-1 text-right ${isStudent ? 'text-indigo-200' : 'text-slate-400'}`}>
                  {fmtMsgTime(msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function ParentDashboard({ nav, setNav, user }) {
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary]   = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [childrenLoading, setChildrenLoading] = useState(true);

  const activeTab = nav || 'dashboard';

  useEffect(() => {
    client.get('/parents/my/children')
      .then(r => {
        const kids = r.data.children || [];
        setChildren(kids);
        if (kids.length) setSelected(kids[0]);
      })
      .catch(() => {})
      .finally(() => setChildrenLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingSummary(true);
    setSummary(null);
    client.get(`/parents/my/children/${selected._id}/summary`)
      .then(r => setSummary(r.data))
      .catch(() => {})
      .finally(() => setLoadingSummary(false));
  }, [selected]);

  if (childrenLoading) return <Spinner />;

  if (!children.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="w-20 h-20 rounded-3xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-5">
          <Users className="w-9 h-9 text-indigo-300" strokeWidth={2} />
        </div>
        <p className="font-sora font-bold text-slate-700 text-lg">No children linked</p>
        <p className="text-sm text-slate-400 mt-2 max-w-xs leading-relaxed">
          Contact your school admin to link your children to this account.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-5">
      {/* Child selector */}
      {children.length > 1 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 px-1">
          {children.map(c => (
            <ChildChip
              key={c._id}
              child={c}
              active={selected?._id === c._id}
              onClick={() => setSelected(c)}
            />
          ))}
        </div>
      )}

      {selected && (
        <>
          {/* Selected child header */}
          <div className="flex items-center gap-3 bg-white rounded-2xl border border-slate-100 px-4 py-3 sm:px-5 sm:py-4">
            <ChildAvatar name={selected.name} size={42} />
            <div className="flex-1 min-w-0">
              <h2 className="font-sora font-bold text-slate-800 text-sm sm:text-base leading-tight truncate">
                {selected.name}
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                Class {selected.class}{selected.section ? ` – ${selected.section}` : ''}
                {selected.relation ? ` · ${selected.relation}` : ''}
              </p>
            </div>
            <div className="hidden sm:flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-100">
              <GraduationCap className="w-3.5 h-3.5 text-indigo-500" strokeWidth={2.5} />
              <span className="text-xs font-semibold text-indigo-600">Class {selected.class}</span>
            </div>
          </div>

          {/* Tab bar + content */}
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <TabBar active={activeTab} onChange={setNav} />

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={`${selected._id}-${activeTab}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="p-3 sm:p-5"
              >
                {activeTab === 'dashboard'     && <OverviewView summary={summary} loading={loadingSummary} />}
                {activeTab === 'attendance'    && <AttendanceView studentId={selected._id} />}
                {activeTab === 'marks'         && <MarksView studentId={selected._id} />}
                {activeTab === 'tests'         && <TestsView studentId={selected._id} />}
                {activeTab === 'assignments'   && <AssignmentsView studentId={selected._id} />}
                {activeTab === 'homework'      && <HomeworkView studentId={selected._id} />}
                {activeTab === 'feedback'      && <FeedbackView studentId={selected._id} />}
                {activeTab === 'announcements' && <AnnouncementsView />}
                {activeTab === 'materials'     && <MaterialsView child={selected} />}
                {activeTab === 'chat'          && <ChatView studentId={selected._id} />}
                {activeTab === 'coaching'      && <CoachingView studentId={selected._id} />}
                {activeTab === 'premium'       && <PremiumView user={user} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </>
      )}
    </div>
  );
}
